import React, { useEffect, useRef, useState } from 'react';
import { useParams,  useSearchParams } from "react-router";
import { useNavigate } from '../../../../src/hooks/useI18nNavigate';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
// UI Components
import { UserItem } from "@edusense/entities-user";
// Social GraphQL Client (팔로워/팔로잉 목록은 GraphQL 사용: 안전한 문자열 ID)
import {
  socialClient,
  useInfiniteGetFollowersByUsernameQuery,
  useInfiniteGetFollowingByUsernameQuery,
} from '@edusense/social-client';

// 사용자 프로필 타입 정의
export interface UserProfile {
  id: string;
  username: string;
  name: string;
  bio?: string;
  profileImage?: string;
  isFollowing?: boolean;
}

export interface FollowsPageProps {
  username?: string; // URL에서 가져오지 않고 props로 받을 경우 사용
  initialType?: 'following' | 'followers';
  isAuthenticated?: boolean;
  currentUser?: { id: string; username: string };
  onFollowToggle?: (username: string, isFollowing: boolean) => Promise<void>;
  onBack?: () => void;
  showToast?: (message: string, type: 'success' | 'error') => void;
  // 목업 데이터(선택 사항)
  mockFollowing?: UserProfile[];
  mockFollowers?: UserProfile[];
}

/**
 * 팔로잉/팔로워 목록 페이지 컴포넌트
 * 
 * 특정 사용자의 팔로잉/팔로워 목록을 표시합니다.
 */
const FollowsPage: React.FC<FollowsPageProps> = ({
  username: propUsername,
  initialType = 'following',
  isAuthenticated = false,
  currentUser,
  onFollowToggle,
  onBack,
  showToast,
  mockFollowing,
  mockFollowers
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // props에서 username을 가져오거나 URL 파라미터에서 가져옴
  const username = propUsername || params.username || '';
  
  // URL 쿼리 파라미터에서 type 가져오기, 기본값은 'following'
  const [type, setType] = useState<'following' | 'followers'>(
    (searchParams.get('type') as 'following' | 'followers') || initialType
  );
  
  // GraphQL 기반 무한 스크롤: username 단일로 목록 + 사용자 정보를 함께 조회
  const followersInfinite = useInfiniteGetFollowersByUsernameQuery(
    socialClient,
    { username, first: 20 },
    {
      enabled: !!username && type === 'followers',
      retry: false,
      getNextPageParam: (lastPage) => {
        const pageInfo = lastPage?.userByUsername?.followers?.pageInfo;
        return pageInfo?.hasNextPage ? { after: pageInfo.endCursor ?? undefined } : undefined;
      },
    }
  );

  const followingInfinite = useInfiniteGetFollowingByUsernameQuery(
    socialClient,
    { username, first: 20 },
    {
      enabled: !!username && type === 'following',
      retry: false,
      getNextPageParam: (lastPage) => {
        const pageInfo = lastPage?.userByUsername?.following?.pageInfo;
        return pageInfo?.hasNextPage ? { after: pageInfo.endCursor ?? undefined } : undefined;
      },
    }
  );

  // 로딩/에러 상태
  const loading = type === 'followers' ? followersInfinite.isLoading : followingInfinite.isLoading;
  const error = type === 'followers' ? followersInfinite.error : followingInfinite.error;

  // 에러 디버깅
  React.useEffect(() => {
    if (error) {
      console.error('[FollowsPage] GraphQL Error:', {
        type,
        username,
        error,
        message: (error as any)?.message,
        graphQLErrors: (error as any)?.response?.errors,
        networkError: (error as any)?.networkError,
      });
    }
  }, [error, type, username]);
  
  // 사용자 목록 변환 (GraphQL 응답을 UI 형식으로)
  const users: UserProfile[] = React.useMemo(() => {
    if (mockFollowing && type === 'following') return mockFollowing;
    if (mockFollowers && type === 'followers') return mockFollowers;

    // GraphQL 응답 기반 매핑 (무한 스크롤 전체 페이지)
    const graphqlEdges = type === 'followers'
      ? (followersInfinite.data?.pages.flatMap(p => p?.userByUsername?.followers?.edges || []) || [])
      : (followingInfinite.data?.pages.flatMap(p => p?.userByUsername?.following?.edges || []) || []);

    // 데이터 디버깅
    if (graphqlEdges.length > 0) {
      console.log('[FollowsPage] GraphQL data mapping:', {
        type,
        edgesCount: graphqlEdges.length,
        firstEdge: graphqlEdges[0],
        sampleNode: graphqlEdges[0]?.node,
      });
    }

    const gqlMapped: UserProfile[] = graphqlEdges
      .filter(edge => edge?.node) // null/undefined 노드 필터링
      .map(edge => ({
        id: edge.node.userId || '',
        username: edge.node.username || '',
        name: edge.node.displayName || edge.node.username || 'Unknown',
        bio: edge.node.bio || undefined,
        profileImage: edge.node.profileImageUrl || undefined,
        isFollowing: edge.node.relationshipWithCurrentUser?.isFollowing ?? false
      }));

    return gqlMapped;
  }, [type, followersInfinite.data, followingInfinite.data, mockFollowing, mockFollowers]);

  // 무한 스크롤: 인터섹션 옵저버로 자동 더 불러오기
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isFetchingNext = type === 'followers' ? followersInfinite.isFetchingNextPage : followingInfinite.isFetchingNextPage;
  const hasNextPage = type === 'followers' ? followersInfinite.hasNextPage : followingInfinite.hasNextPage;
  const fetchNext = type === 'followers' ? followersInfinite.fetchNextPage : followingInfinite.fetchNextPage;

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    if (!hasNextPage) return;
    if (loading) return;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && !isFetchingNext && hasNextPage) {
        fetchNext();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNext, fetchNext, loading, type, username]);
  
  // 목업 사용자 데이터 생성 함수
  const generateMockUsers = (
    targetUsername: string, 
    listType: 'following' | 'followers', 
    count: number
  ): UserProfile[] => {
    // 사용자 이름과 유형에 따라 다른 데이터 생성
    const prefix = listType === 'following' ? 'followed_by_' : 'follower_of_';
    
    return Array.from({ length: count }).map((_, i) => {
      const randomId = Math.random().toString(36).substring(2, 9);
      const userUsername = `${prefix}${targetUsername}_${i+1}`;
      const userName = userUsername
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
      
      return {
        id: `user_${randomId}`,
        username: userUsername,
        name: userName,
        bio: `이것은 ${userName}의 목업 프로필입니다. ${
          listType === 'following' 
            ? `${targetUsername}님이 팔로우하는 사용자입니다.` 
            : `${targetUsername}님을 팔로우하는 사용자입니다.`
        }`,
        profileImage: `https://via.placeholder.com/150/2c3e50/ffffff?text=${userName.charAt(0)}`,
        isFollowing: Math.random() > 0.5 // 랜덤으로 팔로잉 상태 설정
      };
    });
  };
  
  // 뒤로 가기
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };
  
  // 팔로우/언팔로우 처리
  const handleFollowToggle = async (_targetUserId: string, _isFollowing: boolean) => {
    // 이 페이지에서는 기본적으로 팔로우 버튼을 숨기므로 noop
    // 외부에서 onFollowToggle 전달 시에는 상위에서 처리
  };
  
  // 탭 전환
  const handleTabChange = (newType: 'following' | 'followers') => {
    setType(newType);
    
    // URL 쿼리 파라미터 업데이트
    setSearchParams({ type: newType });
  };
  
  // 사용자 아이템 클릭 핸들러
  const handleUserClick = (userId: string) => {
    // 사용자 ID에서 사용자명 찾기
    const user = users.find(u => u.id === userId);
    if (user) {
      navigate(`/profile/${user.username}`);
    }
  };
  
  return (
    <div className="min-h-screen">
      {/* 헤더 */}
      <div className="sticky top-0 z-10 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center px-4 py-3">
          <button onClick={handleBack} className="mr-4">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">
              {username ? (
                type === 'following' ? 
                `${username}님의 팔로잉` : 
                `${username}님의 팔로워`
              ) : (
                type === 'following' ? '팔로잉' : '팔로워'
              )}
            </h1>
          </div>
        </div>
        
        {/* 탭 */}
        <div className="flex">
          <button
            className={`flex-1 py-3 text-center font-medium ${
              type === 'following'
                ? 'border-b-2 border-blue-500 font-bold'
                : 'text-gray-500'
            }`}
            onClick={() => handleTabChange('following')}
          >
            팔로우 중
          </button>
          <button
            className={`flex-1 py-3 text-center font-medium ${
              type === 'followers'
                ? 'border-b-2 border-blue-500 font-bold'
                : 'text-gray-500'
            }`}
            onClick={() => handleTabChange('followers')}
          >
            팔로워
          </button>
        </div>
      </div>
      
      {/* 콘텐츠 */}
      <div className="divide-y divide-gray-200 dark:divide-gray-800">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <div className="text-red-500 mb-2">
              사용자 목록을 불러오는데 실패했습니다.
            </div>
            <div className="text-sm text-gray-500">
              {(error as any)?.message || '네트워크 문제가 발생했습니다. 잠시 후 다시 시도해주세요.'}
            </div>
            <button
              onClick={() => {
                if (type === 'followers') {
                  followersInfinite.refetch();
                } else {
                  followingInfinite.refetch();
                }
              }}
              className="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              다시 시도
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {type === 'following' 
              ? `@${username}님이 팔로우하는 사용자가 없습니다.` 
              : `@${username}님을 팔로우하는 사용자가 없습니다.`}
          </div>
        ) : (
          <div>
            {users.map(user => (
              <UserItem
                key={user.id}
                id={user.id}
                name={user.name}
                username={user.username}
                profileImage={user.profileImage}
                isFollowing={user.isFollowing}
                description={user.bio}
                showFollowButton={isAuthenticated && currentUser && user.username !== currentUser.username}
                onFollowClick={(id: string, isFollowing: boolean) => {
                  handleFollowToggle(id, isFollowing);
                }}
                onClick={handleUserClick}
                className="px-0 py-4"
              />
            ))}
            <div ref={loadMoreRef} className="py-6 flex justify-center items-center">
              {isFetchingNext ? (
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              ) : hasNextPage ? (
                <button
                  onClick={() => fetchNext()}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  더 불러오기
                </button>
              ) : (
                <span className="text-gray-400 text-sm">마지막 페이지</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowsPage; 
